import { API_BASE_URL } from './config';
import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import styles from "./styles/Home.module.css";
import Layout from "./components/Layout";

// LoginModal component with TikTok login
function LoginModal({
  modalView,
  setModalView,
  setShowLoginModal,
  setMessage,
  message,
  form,
  handleChange,
  handleCreateUser,
  handleLogin,
  handleTikTokLogin
}) {
  return (
    <div className={styles.modalOverlay}>
      <div className={styles.modalContent}>
        {modalView === "login" && (
          <>
            <h2>Login to Play</h2>
            <form onSubmit={handleLogin}>
              <input
                type="text"
                name="username"
                placeholder="Username"
                className={styles.input}
                value={form.username}
                onChange={handleChange}
                autoComplete="username"
                required
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className={styles.input}
                value={form.password}
                onChange={handleChange}
                autoComplete="current-password"
                required
              />
              <button type="submit" className={styles.loginButton}>Login</button>
            </form>
            <div className={styles.loginLinks}>
              <button type="button" className={styles.loginLink} onClick={() => { setModalView("create"); setMessage(""); }}>Create New User</button>
              <button type="button" className={styles.loginLink} onClick={() => { setModalView("forgot"); setMessage(""); }}>Forgot Password?</button>
            </div>
            <div className={styles.tiktokAuth}>
              <button className={styles.tiktokButton} onClick={handleTikTokLogin}>
                <img src="/tiktok-logo.svg" alt="TikTok" className={styles.tiktokLogo} />
                Login with TikTok
              </button>
            </div>
            {message && <div className={styles.message}>{message}</div>}
          </>
        )}
        {modalView === "create" && (
          <>
            <h2>Create New User</h2>
            <form onSubmit={handleCreateUser}>
              <input
                type="text"
                name="username"
                placeholder="Username"
                className={styles.input}
                value={form.username}
                onChange={handleChange}
                required
                autoComplete="username"
              />
              <input
                type="email"
                name="email"
                placeholder="Email"
                className={styles.input}
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
              <input
                type="password"
                name="password"
                placeholder="Password"
                className={styles.input}
                value={form.password}
                onChange={handleChange}
                required
                autoComplete="new-password"
              />
              <button type="submit" className={styles.loginButton}>Create Account</button>
            </form>
            <div className={styles.loginLinks}>
              <button type="button" className={styles.loginLink} onClick={() => { setModalView("login"); setMessage(""); }}>Back to Login</button>
            </div>
            {message && <div className={styles.message}>{message}</div>}
          </>
        )}
        {modalView === "forgot" && (
          <>
            <h2>Reset Password</h2>
            <form onSubmit={e => e.preventDefault()}>
              <input
                type="email"
                name="email"
                placeholder="Enter your email"
                className={styles.input}
                value={form.email}
                onChange={handleChange}
                required
                autoComplete="email"
              />
              <button type="submit" className={styles.loginButton}>Send Reset Link</button>
            </form>
            <div className={styles.loginLinks}>
              <button type="button" className={styles.loginLink} onClick={() => { setModalView("login"); setMessage(""); }}>Back to Login</button>
            </div>
            {message && <div className={styles.message}>{message}</div>}
          </>
        )}
        <button className={styles.closeButton} onClick={() => { setShowLoginModal(false); setModalView("login"); setMessage(""); }}>Close</button>
      </div>
    </div>
  );
}

export default function Home() {
  const [showLoginModal, setShowLoginModal] = useState(false);
  const [modalView, setModalView] = useState("login");
  const [form, setForm] = useState({ username: "", password: "", email: "" });
  const [message, setMessage] = useState("");
  const [currentUser, setCurrentUser] = useState(null);

  // Check if user is already logged in on component mount
  useEffect(() => {
    const savedUser = localStorage.getItem("user");
    if (savedUser) {
      try {
        setCurrentUser(JSON.parse(savedUser));
      } catch (e) {
        console.error("Error parsing saved user:", e);
        localStorage.removeItem("user");
      }
    }
  }, []);

  // Handle input changes
  const handleChange = e => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  // Handle TikTok login
  const handleTikTokLogin = async () => {
    setMessage("Redirecting to TikTok login...");
    try {
      const response = await fetch(`${API_BASE_URL}/tiktok-auth.php`, {
        method: 'GET',
        credentials: 'include'
      });
      
      // Check if we got a response at all
      if (response.status === 0) {
        throw new Error('Network error: Cannot connect to server');
      }
      
      const data = await response.json();
      if (data.success) {
        // Redirect to TikTok OAuth
        window.location.href = data.oauth_url;
      } else {
        setMessage('Failed to initiate TikTok login: ' + (data.error || 'Unknown error'));
      }
    } catch (error) {
      console.error('TikTok login error:', error);
      if (error.message.includes('Network error')) {
        setMessage('Cannot connect to server. Make sure your PHP server is running.');
      } else {
        setMessage('TikTok login failed. Please try again.');
      }
    }
  };

  // Handle regular login submit
  const handleLogin = async e => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/login.php`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          username: form.username,
          password: form.password
        })
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
        setMessage("Login successful!");
        setCurrentUser(data.user);
        localStorage.setItem("user", JSON.stringify(data.user));
        setTimeout(() => {
          setShowLoginModal(false);
          window.location.href = "/tiktok-toe";
        }, 1000);
      } else {
        setMessage(data.error || "Invalid credentials");
      }
    } catch (error) {
      console.error("Login error:", error);
      if (error.message.includes('Failed to fetch')) {
        setMessage("Cannot connect to server. Make sure your PHP server is running.");
      } else {
        setMessage("Login failed: " + error.message);
      }
    }
  };

  // Handle create user submit
  const handleCreateUser = async e => {
    e.preventDefault();
    setMessage("");
    try {
      const res = await fetch(`${API_BASE_URL}/createUser.php`, {
        method: "POST",
        headers: { 
          "Content-Type": "application/json",
        },
        credentials: "include",
        body: JSON.stringify({
          username: form.username,
          password: form.password,
          email: form.email
        })
      });
      
      if (!res.ok) {
        throw new Error(`HTTP error! status: ${res.status}`);
      }
      
      const data = await res.json();
      if (data.success) {
        setMessage("User created! You can now log in.");
        setModalView("login");
        setForm({ username: "", password: "", email: "" });
      } else {
        setMessage(data.error || "Error creating user.");
      }
    } catch (error) {
      console.error("Create user error:", error);
      setMessage("Server error: " + error.message);
    }
  };

  // Handle logout
  const handleLogout = () => {
    localStorage.removeItem("user");
    setCurrentUser(null);
    setMessage("Logged out successfully");
    window.location.reload();
  };

  return (
    <Layout
      canonical="tiktactok.cr8tiv.com"
      title="Cr8tiv Digital Agency - TikTok Toe Game"
      description="Play our exciting TikTok Toe game! A TikTok-inspired Tic-Tac-Toe game where you send gifts to make moves."
      keywords="tiktok game, tic-tac-toe, multiplayer game, gift-based game, fun game"
    >
      <div className={styles.homecontainer}>
        <main className={styles.main}>
  

          <Link to="/" title="Creative Creators Network">
            <img
              itemProp="image"
              src="/cr8tiv.svg"
              alt="Creative Creators Network"
              width={800}
              height={220}
              loading="eager"
              className={styles.cr8tiv}
            />
          </Link>
          
          <h1 className={styles.homehead}>
            TikTok Toe - Play Tic-Tac-Toe with Gifts
          </h1>
          
          <p className={styles.h2sub}>
            Experience the exciting new way to play Tic-Tac-Toe! Send TikTok-style gifts to make your moves and outsmart your opponent. 🎮✨
            
            <span className={styles.highlight}>Strategic gameplay</span>, 
            <span className={styles.highlight}>TikTok-themed gifts</span>, and 
            <span className={styles.highlight}>Fun competition</span> — all in one addictive game. 🎁

            Challenge your friends and see who has the best <span className={styles.accent}>GIFT STRATEGY</span>! 🏆
          </p>
        {/* User Welcome Section */}
          {currentUser && (
            <div className={styles.userWelcome}>
              <div className={styles.userInfo}>
                {currentUser.avatar && (
                  <img 
                    src={currentUser.avatar} 
                    alt={currentUser.username}
                    className={styles.userAvatar}
                  />
                )}
                <div className={styles.userDetails}>
                  <h3>Welcome back!</h3>
                  <p className={styles.username}>
                    {currentUser.display_name || currentUser.username}
                    {currentUser.username?.startsWith('tiktok_') && (
                      <span className={styles.tiktokBadge}>@TikTok</span>
                    )}
                  </p>
                </div>
              </div>
              <button onClick={handleLogout} className={styles.logoutButton}>
                Logout
              </button>
            </div>
          )}
          <div className={styles.gridsplash}>
            {[
              {
                title: "Play TikTok Toe Game",
                heading: currentUser ? "PLAY NOW" : "PLAY NOW",
                text: currentUser ? "Continue your game!" : "Experience Tic-Tac-Toe with TikTok gifts!",
                isInternal: true
              },
              {
                title: "How to Play TikTok Toe",
                heading: "HOW TO PLAY",
                text: "Learn the rules and strategies",
                link: "#instructions",
                isInternal: false
              }
            ].map((item, idx) => (
      <div className={styles.homecard} key={idx}>
  {item.isInternal ? (
    <a
      href={currentUser ? "/tiktok-toe" : "#"}
      className={styles.homecardlink}
      onClick={(e) => {
        if (!currentUser) {
          e.preventDefault();
          setShowLoginModal(true);
        }
      }}
    >
      <h3 className={styles.buttitle}>{item.heading}</h3>
      <p className={styles.butsub}>{item.text}</p>
    </a>
  ) : (
    <a href={item.link} className={styles.homecardlink}>
      <h3 className={styles.buttitle}>{item.heading}</h3>
      <p className={styles.butsub}>{item.text}</p>
    </a>
  )}
</div>
            ))}
          </div>
        </main>
      </div>

      <div className={styles.textblock} id="instructions">
        <h2>How to Play TikTok Toe</h2>
        <div className={styles.textblockp}>
          <ol className={styles.instructionsList}>
            <li><strong>Create an Account</strong> - Sign up to save your progress and stats</li>
            <li><strong>Send Gifts</strong> - Choose from different TikTok gifts to make moves</li>
            <li><strong>Select Your Move Gift</strong> - Pick which gift you want to use for your move</li>
            <li><strong>Make Your Move</strong> - Click on an empty square to place your X or O</li>
            <li><strong>Win the Game</strong> - Be the first to get three in a row to win!</li>
          </ol>
        </div>
      </div>

      {showLoginModal && (
        <LoginModal
          modalView={modalView}
          setModalView={setModalView}
          setShowLoginModal={setShowLoginModal}
          setMessage={setMessage}
          message={message}
          form={form}
          handleChange={handleChange}
          handleCreateUser={handleCreateUser}
          handleLogin={handleLogin}
          handleTikTokLogin={handleTikTokLogin}
        />
      )}

      {/* Logout Button at the bottom */}
      {currentUser && (
        <button onClick={handleLogout} className={styles.bottomLogoutButton}>
          Logout
        </button>
      )}
    </Layout>
  );
}